import { Account } from '@radixdlt/radix-dapp-toolkit';
import { LoaderFunctionArgs, json } from '@remix-run/cloudflare';
import { Link, useFetcher, useLoaderData } from '@remix-run/react';
import AccountSelector from '~/components/account-selector';
import { DataBlock } from "~/components/data-block";
import { fetchPrices } from '~/lib/astrolescent';
import { setDbFromContext } from '~/lib/db';
import { getStakingWithToken } from '~/lib/models/staking';
import { getTokens } from '~/lib/models/tokens';
import { classNames, getIconUrl, onIconError, prettyValue } from '~/lib/util';
import { useGatewayApi } from '~/rdt/gateway';
import { loader as walletLoader } from '~/routes/wallet.$account';

export async function loader({ context }: LoaderFunctionArgs) {
   setDbFromContext(context);

   const { state } = useGatewayApi;

	const stakings = await getStakingWithToken();
	const prices = await fetchPrices();
	let totalTVL = 0;

   for (let staking of stakings) {
      console.log(staking);

      // fetch balance
      const poolDetails = await state.getEntityDetailsVaultAggregated(staking.TokenStaking.pool);
      const totalStake = poolDetails.fungible_resources.items[0].vaults.items[0].amount;

		staking.totalStake = totalStake;
		staking.totalValue = totalStake * (prices[staking.Token.address]?.tokenPriceUSD || 0);
		
		totalTVL += staking.totalValue;
   }

   stakings.sort((a, b) => {
      if (a.totalValue < b.totalValue) {
         return 1;
      } else if (a.totalValue > b.totalValue) {
         return -1;
      }
      return 0;
   });

   return json({
      stakings,
      totalTVL,
   });
}

export default function TokensIndexPage() {
   const { stakings, totalTVL } = useLoaderData<typeof loader>();
   const walletFetcher = useFetcher<typeof walletLoader>();

   function onAccount(account: Account) {
      walletFetcher.load(`/wallet/${account.address}`);
   }

   return (
     <div className="spacy-y-4 mx-4 mb-8 xl:mx-auto xl:w-3/5">
       <div className="flex items-center justify-between">
         <h1 className="py-4 text-left text-3xl font-bold lg:p-8 lg:text-4xl">Staking</h1>
       </div>

       <dl className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-4">
         <DataBlock title="Total Value Locked" data={`$${prettyValue(totalTVL)}`} />
         <DataBlock title="Tokens" data={stakings.length} />
       </dl>

       <AccountSelector onAccount={onAccount}></AccountSelector>

       <div className="dark:bg-background-900 mt-8 grow space-y-4 rounded-xl bg-white p-4 text-white lg:rounded-2xl lg:p-8">
         <table className="min-w-full divide-y divide-gray-300">
           <thead>
             <tr>
               <th scope="col" className="text-primary-800 dark:text-primary-100 py-3.5 pl-4 pr-3 text-left text-sm font-semibold sm:pl-0">
                 Token
               </th>
               <th scope="col" className="text-primary-800 dark:text-primary-100 hidden px-3 py-3.5 pl-1 text-left text-sm font-semibold lg:table-cell">
                 Your stake
               </th>
               <th scope="col" className="text-primary-800 dark:text-primary-100 px-3 py-3.5 pl-1 text-left text-sm font-semibold">
                 TVL
               </th>
               <th scope="col" className="text-primary-800 dark:text-primary-100 hidden px-3 py-3.5 pl-1 text-left text-sm font-semibold lg:table-cell"></th>
               <th scope="col" className="text-primary-800 dark:text-primary-100 px-3 py-3.5 pl-1 text-left text-sm font-semibold">
                 APY
               </th>

               <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0"></th>
             </tr>
           </thead>
           <tbody className="divide-primary-300 divide-y">
             {stakings.map((staking) => {
               return (
                 <Link to={`/token/${staking.Token.address}/staking`} key={staking.Token.address} className={classNames("dark:hover:bg-primary-800 table-row align-middle")}>
                   <td className="w-full max-w-0 py-4 pl-4 pr-3 font-bold sm:w-auto sm:max-w-none sm:pl-0">
                     <div className="flex items-center">
                       <img className="h-8 w-8 shrink-0 rounded-full bg-white" src={getIconUrl(staking.Token.iconUrl)} onError={(evt) => onIconError(evt)} alt="" />
                       <div className="ml-3 text-black dark:text-white">
                         {staking.Token.name} <span className="text-primary-800 dark:text-primary-200 block font-normal lg:ml-2 lg:inline">{staking.Token.symbol}</span>
                       </div>
                     </div>
                   </td>
                   <td className="text-primary-800 dark:text-primary-200 hidden px-3 py-4 pl-1 font-medium lg:table-cell">
                     {walletFetcher.data?.filter((b) => b.address == staking.TokenStaking.sToken)[0]?.amount
                       ? prettyValue(walletFetcher.data?.filter((b) => b.address == staking.TokenStaking.sToken)[0]?.amount) + ` ${staking.Token.symbol}`
                       : "-"}
                   </td>

                   <td className="text-primary-800 dark:text-primary-200 px-3 py-4 pl-1 font-medium">
                     {prettyValue(staking.totalStake)} {staking.Token.symbol}
                   </td>
                   <td className="text-primary-800 dark:text-primary-200 hidden px-3 py-4 pl-1 font-medium lg:table-cell">${prettyValue(staking.totalValue)}</td>
                   <td className="text-primary-800 dark:text-primary-200 px-3 py-4 pl-1 font-medium">
                     {(Math.round(((+staking.TokenStaking.intervalAmount * (52 / +staking.TokenStaking.interval)) / staking.totalStake) * 1000) / 10).toLocaleString()}%
                   </td>

                   <td className="hidden py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0 lg:table-cell">
                     <Link to={`/token/${staking.Token.address}/staking`} className="btn-primary-small">
                       Stake
                     </Link>
                   </td>
                 </Link>
               );
             })}
           </tbody>
         </table>
       </div>
     </div>
   );
}
